// WAAP private-portal reads — WAF strategies + derived website list.
//
// These hit the PRIVATE (SSO/portal) backend reads, not the public HMAC config
// areas. The backend returns the raw vendor payload (`any`) — exact field names
// are vendor-defined and best-effort typed here; treat shapes as to-confirm at
// runtime. The `strategies` response is account-wide (one row per protected
// domain, each carrying its `domain`) and doubles as the reliable WAAP website
// list while the public website list 500s server-side.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_READS, type WaapRead } from './_shared';

/** A WAF strategy row from the private portal (`GET /waf_strategies`). One row
 *  per protected domain; `strategy_type` is the managed-policy id applied to it
 *  and `monitor_only` flags the website as being in WAF Monitoring Mode. */
export interface WafStrategyRow {
  id?: number | string;
  domain?: string;
  site_id?: number | string;
  /** Managed protection-policy id (1 = Basic, 3 = Strict, … see WAF_POLICIES). */
  strategy_type?: number | string;
  enable?: boolean;
  monitor_only?: boolean;
  status?: number;
  [k: string]: unknown;
}

/** A strategy→domain mapping row (`GET /waf_strategies/mapping`): which domains
 *  currently run each managed protection policy. */
export interface WafStrategyMappingRow {
  strategyType?: number | string;
  domainList?: string[];
}

/** One stored module toggle inside a saved policy: compact while it tracks its
 *  template, expanded to a rule map once a row was changed. */
export interface StoredModuleSetting {
  enabled: boolean;
  strategy?: number;
  rules?: Record<string, { enabled?: 0 | 1; action?: 1 | 2 }>;
}

/** A custom (user-defined) WAF protection policy, flattened by the API: the
 *  portal nests name/comment/template under `config`, the API lifts them out and
 *  decodes both module shapes so an editor can prefill from this row. */
export interface WafCustomStrategyRow {
  id?: number | string;
  /** Display name of the custom policy. */
  name?: string;
  /** The policy's description (the portal calls it comment). */
  comment?: string;
  /** Template tier 1-3, with its label. */
  template?: number;
  template_label?: string;
  status?: number;
  created?: string;
  updated?: string;
  module_strategies?: Record<string, StoredModuleSetting>;
  syntax_strategies?: Record<string, StoredModuleSetting>;
  /** Domains this custom policy is applied to, when the vendor reports them. */
  domainList?: string[];
  [k: string]: unknown;
}

/** A WAAP-protected website, derived from the strategies feed. */
export interface WaapWebsite {
  domain: string;
}

/** Normalize a raw vendor payload to an array — handles a bare array or a
 *  single-key wrapper (`{ list | data | strategies: [...] }`). Exported so the
 *  account-level pages can flatten the raw strategy reads consistently. */
export function waapAsArray<T = unknown>(raw: unknown): T[] {
  return asArray<T>(raw);
}

function asArray<T = unknown>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    for (const key of ['list', 'data', 'strategies', 'items', 'results']) {
      const v = (raw as Record<string, unknown>)[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

/** Generic private read of a strategies surface. Returns the raw payload. */
function useWaapRead<T = unknown>(domainId: string | undefined, read: WaapRead) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', read),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<T | null> => {
      const res = await serviceApiV3.get<T>(waapPath(domainId as string, read));
      return res.data ?? null;
    },
  });
}

/** Account WAF strategies (one row per protected domain). */
export function useWafStrategies(domainId: string | undefined) {
  return useWaapRead<WafStrategyRow[] | Record<string, unknown>>(domainId, WAAP_READS.strategies);
}

/** Strategy → domain mapping (portal-only). */
export function useWafStrategiesMapping(domainId: string | undefined) {
  return useWaapRead(domainId, WAAP_READS.strategiesMapping);
}

/** Custom (user-defined) WAF strategies (portal-only). */
export function useWafCustomStrategies(domainId: string | undefined) {
  return useWaapRead(domainId, WAAP_READS.customStrategies);
}

/** AI-WAF strategies — backs the AI-WAF / general-protection screen. */
export function useAiWafStrategies(domainId: string | undefined) {
  return useWaapRead(domainId, WAAP_READS.aiWafStrategies);
}

/** The WAAP website list, derived from the account strategies feed (unique,
 *  sorted domains). Used by the website picker / Websites list while the public
 *  website list is unavailable. */
export function useWaapWebsites(domainId: string | undefined) {
  const q = useWafStrategies(domainId);
  const rows = asArray<WafStrategyRow>(q.data);
  const seen = new Set<string>();
  const websites: WaapWebsite[] = [];
  for (const row of rows) {
    const domain = typeof row.domain === 'string' ? row.domain.trim() : '';
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    websites.push({ domain });
  }
  websites.sort((a, b) => a.domain.localeCompare(b.domain));
  return { ...q, websites };
}

// ---- writes (General Protection) -----------------------------------------
// Backed by the private portal via eagle_waf:
//   PUT /strategies/monitor  { site_id, monitor_only }  → /waf_strategy
//   PUT /strategies/apply    { type, site_ids }         → /waf_strategies
// Both are account-level; the domainId only anchors the customer session.

function invalidateStrategyReads(qc: ReturnType<typeof useQueryClient>, domainId: string) {
  qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_READS.strategies) });
  qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_READS.strategiesMapping) });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------
// These mirror the domain-anchored hooks above but build /services/${serviceId}/waap/...
// so General Protection renders without a carrier domain anchor.

function serviceBasePath(serviceId: string, suffix: string): string {
  return `/services/${serviceId}/waap/${suffix}`;
}

export const strategyServiceKeys = {
  strategies: (serviceId: string) => ['waap', 'strategies', 'service', serviceId] as const,
  mapping: (serviceId: string) => ['waap', 'strategies', 'service', serviceId, 'mapping'] as const,
  custom: (serviceId: string) => ['waap', 'strategies', 'service', serviceId, 'custom'] as const,
};

/** Account WAF strategies, resolved from the service (no domain anchor). */
export function useServiceWafStrategies(serviceId: string | undefined) {
  return useQuery({
    queryKey: strategyServiceKeys.strategies(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<WafStrategyRow[] | Record<string, unknown>> => {
      const res = await serviceApiV3.get<WafStrategyRow[] | Record<string, unknown>>(
        serviceBasePath(serviceId as string, 'strategies'),
      );
      return res.data ?? {};
    },
  });
}

/** Strategy → domain mapping, resolved from the service. */
export function useServiceWafStrategiesMapping(serviceId: string | undefined) {
  return useQuery({
    queryKey: strategyServiceKeys.mapping(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<unknown> => {
      const res = await serviceApiV3.get(serviceBasePath(serviceId as string, 'strategies/mapping'));
      return res.data ?? null;
    },
  });
}

/** Custom WAF strategies, resolved from the service. */
export function useServiceWafCustomStrategies(serviceId: string | undefined) {
  return useQuery({
    queryKey: strategyServiceKeys.custom(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<unknown> => {
      const res = await serviceApiV3.get(serviceBasePath(serviceId as string, 'custom-strategies'));
      return res.data ?? null;
    },
  });
}

function invalidateServiceStrategyReads(qc: ReturnType<typeof useQueryClient>, serviceId: string) {
  qc.invalidateQueries({ queryKey: strategyServiceKeys.strategies(serviceId) });
  qc.invalidateQueries({ queryKey: strategyServiceKeys.mapping(serviceId) });
}

/** Toggle a website's WAF Monitoring Mode, service-scoped. */
export function useSetServiceWafMonitor(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { site_id: number; monitor_only: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { site_id: number; monitor_only: boolean }) => {
      const res = await serviceApiV3.put(
        serviceBasePath(serviceId, 'strategies/monitor'),
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceStrategyReads(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Apply a managed WAF protection policy, service-scoped. */
export function useApplyServiceWafPolicy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { type: number; site_ids: number[] }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { type: number; site_ids: number[] }) => {
      const res = await serviceApiV3.put(
        serviceBasePath(serviceId, 'strategies/apply'),
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceStrategyReads(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Toggle a website's WAF Monitoring Mode by its portal site id. */
export function useSetWafMonitor(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { site_id: number; monitor_only: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { site_id: number; monitor_only: boolean }) => {
      const res = await serviceApiV3.put(
        `/waap/domains/${domainId}/strategies/monitor`,
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategyReads(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Apply a managed WAF protection policy (strategy_type) to a set of site ids. */
export function useApplyWafPolicy(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { type: number; site_ids: number[] }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { type: number; site_ids: number[] }) => {
      const res = await serviceApiV3.put(
        `/waap/domains/${domainId}/strategies/apply`,
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategyReads(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Custom WAF Protection Policy ----------------------------------------
// All service-scoped, and all of the policy shape now lives server-side:
//
//   GET    waf-policy-templates            the three templates expanded
//   GET    waf-rules?template=N            rule catalog annotated for a template
//   POST   custom-strategies               create
//   PUT    custom-strategies/{id}          update (replaces the whole config)
//   DELETE custom-strategies/{id}          delete
//
// The catalog comes back already grouped, with the module key per tag, whether
// the template enables it (only enabled groups get a tab), and per rule whether
// it is ticked and what its Action is. The form therefore never has to know the
// module list or which rule level belongs to which template.

export const CUSTOM_TEMPLATES = [
  { value: '1', label: 'Basic' },
  { value: '2', label: 'Standard' },
  { value: '3', label: 'Strict' },
] as const;

export type CustomTemplate = '1' | '2' | '3';

/** A rule's Action column: Monitor records, Block stops the request. */
export const ACTION_MONITOR = 1;
export const ACTION_BLOCK = 2;
export type WafRuleAction = typeof ACTION_MONITOR | typeof ACTION_BLOCK;

/** One managed rule as the annotated catalog returns it. `summary` and
 *  `effectScope` are HTML strings (<br/>-separated). */
export interface WafCatalogRule {
  id: number;
  name?: string;
  riskLevel?: string;
  strategyLevel?: number;
  /** Basic | Standard | Strict | Caution, derived from strategyLevel. */
  securityLevel?: string;
  summary?: string;
  effectScope?: string;
  /** Ticked under the current policy. */
  selected: boolean;
  action: WafRuleAction;
  actionLabel: string;
  /** Set on Semantics Protection rows: the syntax toggle they map to. */
  syntaxKey?: string;
}

/** One catalog tag annotated against a policy — a tab in the form. */
export interface WafCatalogGroup {
  tag: string;
  module?: string;
  kind: 'module' | 'syntax' | 'unmapped';
  enabled: boolean;
  expanded: boolean;
  strategy: number;
  selected: number;
  total: number;
  selected_rule_ids: number[];
  rules: WafCatalogRule[];
}

export interface WafRuleCatalog {
  template: number;
  groups: WafCatalogGroup[];
}

/** A per-rule override sent for a module the user touched. */
export interface WafRuleOverride {
  enabled?: 0 | 1;
  action?: WafRuleAction;
}

/** A per-module override. Sending `rules` switches that module to the expanded
 *  shape the portal uses once a row is unticked or set to Monitor. */
export interface WafModuleOverride {
  enabled?: boolean;
  strategy?: number;
  rules?: Record<string, WafRuleOverride>;
}

/** Create / update body. Name, comment and template are enough for a policy that
 *  tracks its template; overrides carry the rows the user changed. */
export interface CustomPolicyPayload {
  name: string;
  comment: string;
  template: CustomTemplate;
  module_strategies?: Record<string, WafModuleOverride>;
  syntax_strategies?: Record<string, WafModuleOverride>;
}

const policyKeys = {
  catalog: (serviceId: string, template: string) =>
    ['waap', 'waf-rules', 'service', serviceId, template] as const,
};

/** Managed WAF rule catalog, annotated against a policy template. */
export function useServiceWafCatalog(serviceId: string | undefined, template: CustomTemplate) {
  return useQuery({
    queryKey: policyKeys.catalog(serviceId ?? '', template),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<WafRuleCatalog> => {
      const res = await serviceApiV3.get<WafRuleCatalog>(
        serviceBasePath(serviceId as string, 'waf-rules'),
        { params: { template } },
      );
      return res.data ?? { template: Number(template), groups: [] };
    },
  });
}

/** Create a Custom WAF Protection Policy. */
export function useCreateServiceCustomPolicy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, CustomPolicyPayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CustomPolicyPayload) => {
      const res = await serviceApiV3.post(serviceBasePath(serviceId, 'custom-strategies'), payload);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: strategyServiceKeys.custom(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a Custom WAF Protection Policy — the whole config is replaced. */
export function useUpdateServiceCustomPolicy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { id: string | number; payload: CustomPolicyPayload }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string | number; payload: CustomPolicyPayload }) => {
      const res = await serviceApiV3.put(
        serviceBasePath(serviceId, `custom-strategies/${id}`),
        payload,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: strategyServiceKeys.custom(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a Custom WAF Protection Policy. */
export function useDeleteServiceCustomPolicy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string | number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | number) => {
      const res = await serviceApiV3.delete(serviceBasePath(serviceId, `custom-strategies/${id}`));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: strategyServiceKeys.custom(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
